import React, { useEffect, useState, Component } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { UniversalSearchBar } from './UniversalSearchBar';
import { BarChartIcon, CreditCardIcon, LogOutIcon, MenuIcon, XIcon, LayoutGridIcon, TerminalIcon, FileTextIcon, CalendarIcon, UsersIcon, ArrowLeftRightIcon, ShoppingBagIcon, ShoppingCartIcon, SettingsIcon, GlobeIcon, GraduationCapIcon, ChevronDownIcon, SmartphoneIcon, BanknoteIcon, MegaphoneIcon, HomeIcon, BedDoubleIcon, ClipboardListIcon, DollarSignIcon, BookOpenIcon, TruckIcon, FileIcon, AlertTriangleIcon, RefreshCwIcon, ShieldIcon, BarChart3Icon, HelpCircleIcon, BellIcon, PlusIcon, LinkIcon, RepeatIcon } from 'lucide-react';
interface AppLayoutProps {
  children: React.ReactNode;
}
export function AppLayout({
  children
}: AppLayoutProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [terminalsExpanded, setTerminalsExpanded] = useState(false);
  const [transactionsExpanded, setTransactionsExpanded] = useState(false);
  const [reportingExpanded, setReportingExpanded] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [createMenuOpen, setCreateMenuOpen] = useState(false);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const {
    logout
  } = useAuth();
  const placeholderTexts = ['How do I activate new payment methods?', 'How long does it take to receive a verdict on a dispute?', 'Can I refund a successful transaction from the past week?', "How long can I place a temporary hold on a customer's card for?"];
  // Rotate placeholder text every 3 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setPlaceholderIndex(prev => (prev + 1) % placeholderTexts.length);
    }, 3000);
    return () => clearInterval(interval);
  }, []);
  const isActive = (path: string) => {
    if (path === '/overview') {
      return location.pathname === '/overview' || location.pathname === '/';
    }
    return location.pathname === path;
  };
  const isPaymentRequestsActive = () => {
    return location.pathname === '/payment-requests';
  };
  // Check if any transactions path is active
  const isTransactionsActive = () => {
    return ['/sales', '/settlements', '/disputes', '/refunds', '/payment-analytics'].some(path => location.pathname.startsWith(path));
  };
  // Check if any reporting path is active
  const isReportingActive = () => {
    return ['/reports', '/custom-metrics', '/nadi'].some(path => location.pathname.startsWith(path));
  };
  // Get the current page title based on the URL
  const getPageTitle = () => {
    if (location.pathname === '/overview' || location.pathname === '/') return 'Overview';
    if (location.pathname === '/payment-requests') return 'Payment Requests';
    if (location.pathname === '/settings') return 'Settings';
    if (location.pathname === '/virtual-terminal') return 'Virtual Terminal';
    if (location.pathname === '/physical-terminals') return 'Physical Terminals';
    if (location.pathname === '/softpos-terminals') return 'SoftPos Terminals';
    if (location.pathname === '/connect') return 'Connect OAuth2 Apps';
    if (location.pathname === '/orders') return 'Orders';
    if (location.pathname === '/tutorials') return 'Tutorials';
    if (location.pathname === '/campaigns') return 'Campaigns';
    if (location.pathname === '/campaign-details') return 'Campaign Details';
    if (location.pathname === '/campaign-form') return location.search.includes('id=') ? 'Update Campaign' : 'Create Campaign';
    if (location.pathname === '/invoices') return 'Invoices';
    if (location.pathname.startsWith('/invoice-detail/')) return 'Invoice Details';
    if (location.pathname === '/invoice-form') return location.search.includes('id=') ? 'Edit Invoice' : 'Create Invoice';
    if (location.pathname === '/customers') return 'Customers';
    if (location.pathname.startsWith('/customer-detail/')) return 'Customer Details';
    if (location.pathname === '/customer-form') return location.search.includes('id=') ? 'Edit Customer' : 'Create Customer';
    if (location.pathname === '/sales') return 'Sales';
    if (location.pathname.startsWith('/sale-detail/')) return 'Sale Details';
    if (location.pathname === '/settlements') return 'Settlements';
    if (location.pathname.startsWith('/settlement-detail/')) return 'Settlement Details';
    if (location.pathname === '/disputes') return 'Disputes';
    if (location.pathname.startsWith('/dispute-detail/')) return 'Dispute Details';
    if (location.pathname === '/refunds') return 'Refunds';
    if (location.pathname.startsWith('/refund-detail/')) return 'Refund Details';
    if (location.pathname === '/suppliers') return 'Suppliers';
    if (location.pathname.startsWith('/supplier-detail/')) return 'Supplier Details';
    if (location.pathname === '/supplier-form') return location.search.includes('id=') ? 'Edit Supplier' : 'Create Supplier';
    if (location.pathname === '/bills') return 'Bills';
    if (location.pathname.startsWith('/bill-detail/')) return 'Bill Details';
    if (location.pathname === '/bill-form') return location.search.includes('id=') ? 'Edit Bill' : 'Create Bill';
    if (location.pathname.startsWith('/bill-approval/')) return 'Bill Approval';
    if (location.pathname.startsWith('/bill-financing/')) return 'Request Bill Financing';
    if (location.pathname.startsWith('/tutorial/')) return 'Tutorial';
    if (location.pathname === '/reports') return 'Reports';
    if (location.pathname === '/custom-metrics') return 'Custom Metrics';
    if (location.pathname === '/nadi') return 'Nadi';
    return 'Dashboard';
  };
  return <div className="min-h-screen bg-gray-50 flex flex-col" data-id="element-985">
      {/* Sidebar - Now extends to top */}
      <div className={`
        fixed inset-y-0 left-0 w-72 bg-gray-100 border-r border-gray-300 z-20 transform transition-transform duration-200 ease-in-out
        ${menuOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `} data-id="element-986">
        <div className="h-full flex flex-col" data-id="element-987">
          {/* Logo */}
          <div className="p-4 border-b border-gray-300 flex items-center justify-center flex-shrink-0" data-id="element-988">
            <img src="https://cdn.magicpatterns.com/uploads/txoPdqzVAwx9WbLZTFyv98/alliancelogo.svg" alt="Alliance Bank" className="h-16 w-auto" data-id="element-989" />
          </div>

          {/* Navigation */}
          <nav className="flex-1 py-4 space-y-1 text-gray-700 overflow-y-auto" data-id="element-990">
            <button onClick={() => navigate('/overview')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/overview') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-991">
              <BarChartIcon size={18} className="mr-3" data-id="element-992" />
              Overview
            </button>
            <button onClick={() => navigate('/payment-requests')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/payment-requests') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-993">
              <LayoutGridIcon size={18} className="mr-3" data-id="element-994" />
              Payment requests
            </button>
            {/* Terminals Collapsible Menu */}
            <div data-id="element-995">
              <button onClick={() => setTerminalsExpanded(!terminalsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isActive('/virtual-terminal') || isActive('/physical-terminals') || isActive('/softpos-terminals') || isActive('/connect') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-996">
                <div className="flex items-center" data-id="element-997">
                  <TerminalIcon size={18} className="mr-3" data-id="element-998" />
                  Terminals
                </div>
                <ChevronDownIcon size={16} className={`transition-transform ${terminalsExpanded ? 'rotate-180' : ''}`} data-id="element-999" />
              </button>
              {/* Terminal submenu items */}
              {terminalsExpanded && <div className="ml-7 pl-4 border-l border-gray-300" data-id="element-1000">
                  <button onClick={() => navigate('/virtual-terminal')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/virtual-terminal') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1001">
                    <BanknoteIcon size={16} className="mr-3" data-id="element-1002" />
                    Virtual
                  </button>
                  <button onClick={() => navigate('/physical-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/physical-terminals') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1003">
                    <CreditCardIcon size={16} className="mr-3" data-id="element-1004" />
                    Physical
                  </button>
                  <button onClick={() => navigate('/softpos-terminals')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/softpos-terminals') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1005">
                    <SmartphoneIcon size={16} className="mr-3" data-id="element-1006" />
                    SoftPos
                  </button>
                  <button onClick={() => navigate('/connect')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/connect') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1007">
                    <GlobeIcon size={16} className="mr-3" data-id="element-1008" />
                    Connect
                  </button>
                </div>}
            </div>
            <button onClick={() => navigate('/invoices')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/invoices') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1009">
              <FileTextIcon size={18} className="mr-3" data-id="element-1010" />
              Invoices
            </button>
            <button onClick={() => navigate('/bills')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/bills') || location.pathname.startsWith('/bill-detail/') || isActive('/bill-form') || location.pathname.startsWith('/bill-approval/') || location.pathname.startsWith('/bill-financing/') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1011">
              <FileIcon size={18} className="mr-3" data-id="element-1012" />
              Bills
            </button>
            <div className="border-t border-gray-300" data-id="element-1013"></div>
            <button onClick={() => navigate('/customers')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/customers') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1014">
              <UsersIcon size={18} className="mr-3" data-id="element-1015" />
              Customers
            </button>
            <button onClick={() => navigate('/suppliers')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/suppliers') || location.pathname.startsWith('/supplier-detail/') || isActive('/supplier-form') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1016">
              <TruckIcon size={18} className="mr-3" data-id="element-1017" />
              Suppliers
            </button>
            <div className="border-t border-gray-300" data-id="element-1018"></div>
            <button onClick={() => navigate('/balance')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/balance') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1019">
              <BanknoteIcon size={18} className="mr-3" data-id="element-1020" />
              Balance
            </button>
            <button onClick={() => setTransactionsExpanded(!transactionsExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isTransactionsActive() ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1021">
              <div className="flex items-center" data-id="element-1022">
                <ArrowLeftRightIcon size={18} className="mr-3" data-id="element-1023" />
                Transactions
              </div>
              <ChevronDownIcon size={16} className={`transition-transform ${transactionsExpanded ? 'rotate-180' : ''}`} data-id="element-1024" />
            </button>
            {/* Transactions submenu items */}
            {transactionsExpanded && <div className="ml-7 pl-4 border-l border-gray-300" data-id="element-1025">
                <button onClick={() => navigate('/payment-analytics')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/payment-analytics') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1026">
                  <BarChart3Icon size={16} className="mr-3" data-id="element-1027" />
                  Payment analytics
                </button>
                <button onClick={() => navigate('/sales')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/sales') || location.pathname.startsWith('/sale-detail/') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1028">
                  <CreditCardIcon size={16} className="mr-3" data-id="element-1029" />
                  Sales
                </button>
                <button onClick={() => navigate('/settlements')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/settlements') || location.pathname.startsWith('/settlement-detail/') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1030">
                  <BanknoteIcon size={16} className="mr-3" data-id="element-1031" />
                  Settlements
                </button>
                <button onClick={() => navigate('/disputes')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/disputes') || location.pathname.startsWith('/dispute-detail/') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1032">
                  <AlertTriangleIcon size={16} className="mr-3" data-id="element-1033" />
                  Disputes
                </button>
                <button onClick={() => navigate('/refunds')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/refunds') || location.pathname.startsWith('/refund-detail/') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1034">
                  <RefreshCwIcon size={16} className="mr-3" data-id="element-1035" />
                  Refunds
                </button>
              </div>}
            <button onClick={() => navigate('/shops')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/shops') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1036">
              <ShoppingBagIcon size={18} className="mr-3" data-id="element-1037" />
              Shops
            </button>
            <button onClick={() => navigate('/orders')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/orders') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1038">
              <ShoppingCartIcon size={18} className="mr-3" data-id="element-1039" />
              Orders
            </button>
            <button onClick={() => navigate('/campaigns')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/campaigns') || isActive('/campaign-details') || isActive('/campaign-form') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1040">
              <MegaphoneIcon size={18} className="mr-3" data-id="element-1041" />
              Campaigns
            </button>
            <div className="border-t border-gray-300" data-id="element-1042"></div>
            <button onClick={() => setReportingExpanded(!reportingExpanded)} className={`w-full flex items-center justify-between px-4 py-3 text-sm font-medium ${isReportingActive() ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1043">
              <div className="flex items-center" data-id="element-1044">
                <BarChartIcon size={18} className="mr-3" data-id="element-1045" />
                Reporting
              </div>
              <ChevronDownIcon size={16} className={`transition-transform ${reportingExpanded ? 'rotate-180' : ''}`} data-id="element-1046" />
            </button>
            {/* Reporting submenu items */}
            {reportingExpanded && <div className="ml-7 pl-4 border-l border-gray-300" data-id="element-1047">
                <button onClick={() => navigate('/reports')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/reports') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1048">
                  <FileTextIcon size={16} className="mr-3" data-id="element-1049" />
                  Reports
                </button>
                <button onClick={() => navigate('/custom-metrics')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/custom-metrics') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1050">
                  <BarChart3Icon size={16} className="mr-3" data-id="element-1051" />
                  Custom metrics
                </button>
                <button onClick={() => navigate('/nadi')} className={`w-full flex items-center px-4 py-2 text-sm font-medium ${isActive('/nadi') ? 'text-[#003366] font-semibold' : 'text-gray-600 hover:text-gray-900'}`} data-id="element-1052">
                  <BarChartIcon size={16} className="mr-3" data-id="element-1053" />
                  Nadi
                </button>
              </div>}
            <button onClick={() => navigate('/settings')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/settings') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1054">
              <SettingsIcon size={18} className="mr-3" data-id="element-1055" />
              Settings
            </button>
            <button onClick={() => navigate('/tutorials')} className={`w-full flex items-center px-4 py-3 text-sm font-medium ${isActive('/tutorials') ? 'bg-[#003366] text-white' : 'hover:bg-gray-200'}`} data-id="element-1056">
              <GraduationCapIcon size={18} className="mr-3" data-id="element-1057" />
              Tutorials
            </button>
          </nav>
        </div>
      </div>

      {/* Top Header Bar - Logo, Search, User in one row */}
      <div className="fixed top-0 left-0 right-0 bg-white border-b border-gray-200 z-30 lg:left-72" data-id="element-1058">
        <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 py-3" data-id="element-1059">
          {/* Logo - Only visible on mobile */}
          <div className="lg:hidden" data-id="element-1060">
            <img src="https://cdn.magicpatterns.com/uploads/txoPdqzVAwx9WbLZTFyv98/alliancelogo.svg" alt="Alliance Bank" className="h-12 w-auto" data-id="element-1061" />
          </div>

          {/* Search Bar - Center/Flex grow */}
          <div className="flex-1 max-w-2xl mx-4" data-id="element-1062">
            <UniversalSearchBar data-id="element-1063" />
          </div>

          {/* Right side icons - Help, Notifications, Create Menu, User Menu */}
          <div className="flex items-center gap-2" data-id="element-1064">
            {/* Help Icon */}
            <button onClick={() => setAssistantOpen(!assistantOpen)} className="p-2 rounded-lg hover:bg-gray-100 transition-colors relative group" data-id="element-1065">
              <HelpCircleIcon size={20} className="text-gray-600" data-id="element-1066" />
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1067">
                Help
              </span>
            </button>

            {/* Notifications Icon */}
            <button onClick={() => {
            /* TODO: Open notifications panel */
          }} className="p-2 rounded-lg hover:bg-gray-100 transition-colors relative group" data-id="element-1068">
              <BellIcon size={20} className="text-gray-600" data-id="element-1069" />
              {/* Notification badge */}
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-blue-600 rounded-full" data-id="element-1070"></span>
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1071">
                Notifications
              </span>
            </button>

            {/* Create Menu (+ Icon) */}
            <div className="relative group" data-id="element-1072">
              <button onClick={() => setCreateMenuOpen(!createMenuOpen)} className="p-2 rounded-full bg-blue-600 hover:bg-blue-700 transition-colors" data-id="element-1073">
                <PlusIcon size={20} className="text-white" data-id="element-1074" />
              </button>
              <span className="absolute top-full left-1/2 -translate-x-1/2 mt-2 px-2 py-1 bg-gray-900 text-white text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none" data-id="element-1075">
                Create
              </span>
              {/* Create Dropdown Menu */}
              {createMenuOpen && <>
                  <div className="fixed inset-0 z-10" onClick={() => setCreateMenuOpen(false)} data-id="element-1076" />
                  <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-20" data-id="element-1077">
                    <button onClick={() => {
                  setCreateMenuOpen(false);
                  navigate('/invoice-form');
                }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1078">
                      <div className="flex items-center" data-id="element-1079">
                        <FileTextIcon size={18} className="mr-3 text-gray-500" data-id="element-1080" />
                        <span className="font-medium" data-id="element-1081">Invoice</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1082">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1083">
                          ⌘
                        </kbd>
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1084">
                          I
                        </kbd>
                      </div>
                    </button>
                    <button onClick={() => {
                  setCreateMenuOpen(false);
                  navigate('/payment-requests?tab=recurring');
                }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1085">
                      <div className="flex items-center" data-id="element-1086">
                        <RepeatIcon size={18} className="mr-3 text-gray-500" data-id="element-1087" />
                        <span className="font-medium" data-id="element-1088">Subscription</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1089">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1090">
                          ⌘
                        </kbd>
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1091">
                          S
                        </kbd>
                      </div>
                    </button>
                    <button onClick={() => {
                  setCreateMenuOpen(false);
                  navigate('/payment-requests?tab=single');
                }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1092">
                      <div className="flex items-center" data-id="element-1093">
                        <LinkIcon size={18} className="mr-3 text-gray-500" data-id="element-1094" />
                        <span className="font-medium" data-id="element-1095">Payment link</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1096">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1097">
                          ⌘
                        </kbd>
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1098">
                          L
                        </kbd>
                      </div>
                    </button>
                    <button onClick={() => {
                  setCreateMenuOpen(false);
                  navigate('/payment-requests?tab=charge');
                }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1099">
                      <div className="flex items-center" data-id="element-1100">
                        <CreditCardIcon size={18} className="mr-3 text-gray-500" data-id="element-1101" />
                        <span className="font-medium" data-id="element-1102">Charge</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1103">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1104">
                          ⌘
                        </kbd>
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1105">
                          P
                        </kbd>
                      </div>
                    </button>
                    <button onClick={() => {
                  setCreateMenuOpen(false);
                  navigate('/payment-requests?tab=security-hold');
                }} className="w-full flex items-center justify-between px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50" data-id="element-1106">
                      <div className="flex items-center" data-id="element-1107">
                        <ShieldIcon size={18} className="mr-3 text-gray-500" data-id="element-1108" />
                        <span className="font-medium" data-id="element-1109">Security hold</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-gray-400" data-id="element-1110">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1111">
                          ⌘
                        </kbd>
                        <kbd className="px-1.5 py-0.5 bg-gray-100 rounded" data-id="element-1112">
                          O
                        </kbd>
                      </div>
                    </button>
                  </div>
                </>}
            </div>

            {/* User Menu */}
            <div className="relative" data-id="element-1113">
              <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center hover:bg-gray-50 rounded-lg px-2 py-1 transition-colors" data-id="element-1114">
                <div className="w-10 h-10 bg-[#f8d7d7] rounded-full flex items-center justify-center text-[#e54c3c] font-medium" data-id="element-1115">
                  SV
                </div>
                <div className="ml-3 hidden sm:block text-left" data-id="element-1116">
                  <p className="text-sm font-medium text-gray-800" data-id="element-1117">
                    Simon Verraest
                  </p>
                  <p className="text-xs text-gray-500" data-id="element-1118">SMART SDN BHD</p>
                </div>
                <ChevronDownIcon size={16} className={`ml-2 text-gray-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} data-id="element-1119" />
              </button>
              {/* User Dropdown Menu */}
              {userMenuOpen && <>
                  <div className="fixed inset-0 z-10" onClick={() => setUserMenuOpen(false)} data-id="element-1120" />
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-20" data-id="element-1121">
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings?tab=company');
                }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-1122">
                      <UsersIcon size={16} className="mr-3 text-gray-500" data-id="element-1123" />
                      Account
                    </button>
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings?tab=company');
                }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-1124">
                      <BarChartIcon size={16} className="mr-3 text-gray-500" data-id="element-1125" />
                      Report
                    </button>
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings');
                }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-1126">
                      <SettingsIcon size={16} className="mr-3 text-gray-500" data-id="element-1127" />
                      Settings
                    </button>
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings?tab=security');
                }} className="w-full flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100" data-id="element-1128">
                      <ShieldIcon size={16} className="mr-3 text-gray-500" data-id="element-1129" />
                      Security
                    </button>
                    <div className="border-t border-gray-200 my-1" data-id="element-1130" />
                    <button onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                  navigate('/');
                }} className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50" data-id="element-1131">
                      <LogOutIcon size={16} className="mr-3" data-id="element-1132" />
                      Logout
                    </button>
                  </div>
                </>}
            </div>
          </div>
        </div>
      </div>

      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-16 right-0 p-4 z-20" data-id="element-1133">
        <button onClick={() => setMenuOpen(!menuOpen)} className="p-2 rounded-md bg-white shadow-sm border border-gray-200" data-id="element-1134">
          {menuOpen ? <XIcon size={20} data-id="element-1135" /> : <MenuIcon size={20} data-id="element-1136" />}
        </button>
      </div>

      {/* Main content */}
      <div className="flex-1 lg:pl-72 pt-16" data-id="element-1137">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6" data-id="element-1138">
          {children}
        </div>
      </div>

      {/* AI Assistant Panel */}
      {assistantOpen && <>
          {/* Overlay */}
          <div className="fixed inset-0 bg-black bg-opacity-30 z-40 transition-opacity" onClick={() => setAssistantOpen(false)} data-id="element-1139" />

          {/* Assistant Panel */}
          <div className="fixed top-0 right-0 h-full w-96 bg-white shadow-2xl z-50 flex flex-col" data-id="element-1140">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200" data-id="element-1141">
              <div className="flex items-center gap-2" data-id="element-1142">
                <div className="w-8 h-8 bg-[#003366] rounded-full flex items-center justify-center" data-id="element-1143">
                  <span className="text-white text-sm font-semibold" data-id="element-1144">✨</span>
                </div>
                <h2 className="text-lg font-semibold text-gray-900" data-id="element-1145">
                  Assistant
                </h2>
              </div>
              <button onClick={() => setAssistantOpen(false)} className="p-1 hover:bg-gray-100 rounded transition-colors" data-id="element-1146">
                <XIcon size={20} className="text-gray-500" data-id="element-1147" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4" data-id="element-1148">
              <div className="space-y-4" data-id="element-1149">
                {/* Welcome Message */}
                <div className="bg-gray-50 rounded-lg p-4" data-id="element-1150">
                  <p className="text-sm text-gray-700 mb-2" data-id="element-1151">
                    Hi, I'm Alliance Bank Assistant. 👋
                  </p>
                  <p className="text-sm text-gray-600" data-id="element-1152">
                    What do you need help with? Select a topic or type your
                    question below.
                  </p>
                </div>

                {/* Quick Action Buttons */}
                <div className="space-y-2" data-id="element-1153">
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50 transition-colors" data-id="element-1154">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1155">
                      Contact merchant support
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50 transition-colors" data-id="element-1156">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1157">
                      My verification status
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50 transition-colors" data-id="element-1158">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1159">
                      Change business type
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50 transition-colors" data-id="element-1160">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1161">
                      My settlement is missing
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50 transition-colors" data-id="element-1162">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1163">
                      Update bank account for settlement
                    </p>
                  </button>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50 transition-colors" data-id="element-1164">
                    <p className="text-sm font-medium text-gray-900" data-id="element-1165">
                      Malaysia payment regulations
                    </p>
                  </button>
                </div>

                {/* Recent Question */}
                <div className="pt-4 border-t border-gray-200" data-id="element-1166">
                  <p className="text-xs font-medium text-gray-500 mb-2" data-id="element-1167">
                    RECENT
                  </p>
                  <button className="w-full text-left px-4 py-3 border border-gray-200 rounded-lg hover:border-[#003366] hover:bg-blue-50 transition-colors" data-id="element-1168">
                    <p className="text-sm text-gray-700" data-id="element-1169">
                      How do I enable FPX for my customers?
                    </p>
                  </button>
                </div>
              </div>
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-200" data-id="element-1170">
              <div className="relative" data-id="element-1171">
                <input type="text" placeholder={placeholderTexts[placeholderIndex]} className="w-full px-4 py-3 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003366] focus:border-transparent transition-all" key={placeholderIndex} data-id="element-1172" />
                <button className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-gray-600" data-id="element-1173">
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor" data-id="element-1174">
                    <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" data-id="element-1175" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </>}

      {/* Overlay for mobile */}
      {menuOpen && <div className="fixed inset-0 bg-black bg-opacity-50 z-0 lg:hidden" onClick={() => setMenuOpen(false)} data-id="element-1176"></div>}
    </div>;
}